//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import LocationImportHelper from 'common/entity/locationMaster/LocationImportHelper';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';

const multiDaySlotSpan = {
    titleKey: 'masterLocationProperties.multiDaySlotSpan',
    id: 'multiDaySlotSpan',
    templateValue: 'masterImportTemplateValues.masterLocation.multiDaySlotSpan'
};

const addTimeSlots = defaultColumns => {
    const slotColumns = Object.values(DayOfWeekType).map(dayOfWeek => ({
        id: dayOfWeek,
        titleKey: 'masterLocationProperties.operatingSlots',
        titleId: dayOfWeek,
        accessor: ['timeSlots', dayOfWeek],
        templateValue:
            'masterImportTemplateValues.masterLocation.operatingSlots',
        processCell: LocationImportHelper.processTimeSlots,
        validateCell: LocationImportHelper.validateEntityTimeSlots,
        serializeCell: LocationImportHelper.serializeMultiDaySlot,
        getModelFieldAccessor: LocationImportHelper.getTimeSlotsAccessor
    }));
    const columns = [...defaultColumns, ...slotColumns, multiDaySlotSpan];
    return columns;
};

const LOCATION_IMPORT_FORMAT: Array<TableColumnTypes> = addTimeSlots([
    {
        titleKey: 'masterLocationProperties.locationId',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterLocation.locationId'
    },
    {
        titleKey: 'masterLocationProperties.locationCode',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterLocation.locationCode'
    },
    {
        titleKey: 'masterLocationProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterLocation.name'
    },
    {
        titleKey: 'masterLocationProperties.type',
        id: 'type',
        required: true,
        templateValue: 'masterImportTemplateValues.masterLocation.type',
        processCell: LocationImportHelper.processUpperCaseId
    },
    {
        titleKey: 'masterLocationProperties.teamId',
        id: 'teams',
        templateValue: 'masterImportTemplateValues.masterLocation.teams',
        processCell: LocationImportHelper.processTeams,
        validateCell: LocationImportHelper.validateTeamIds,
        serializeCell: LocationImportHelper.serializeTeams
    },
    {
        titleKey: 'masterLocationProperties.address.placeName',
        id: 'placeName',
        accessor: ['address', 'placeName'],
        templateValue:
            'masterImportTemplateValues.masterLocation.address.placeName'
    },
    {
        titleKey: 'masterLocationProperties.address.address',
        id: 'formattedAddress',
        accessor: ['address', 'formattedAddress'],
        required: true,
        templateValue:
            'masterImportTemplateValues.masterLocation.address.address'
    },
    {
        titleKey: 'masterLocationProperties.address.locality',
        id: 'localityName',
        accessor: ['address', 'localityName'],
        templateValue:
            'masterImportTemplateValues.masterLocation.address.locality'
    },
    {
        titleKey: 'masterLocationProperties.address.city',
        id: 'city',
        accessor: ['address', 'city'],
        templateValue: 'masterImportTemplateValues.masterLocation.address.city'
    },
    {
        titleKey: 'masterLocationProperties.address.state',
        id: 'state',
        accessor: ['address', 'state'],
        templateValue: 'masterImportTemplateValues.masterLocation.address.state'
    },
    {
        titleKey: 'masterLocationProperties.address.pincode',
        id: 'pincode',
        accessor: ['address', 'pincode'],
        templateValue:
            'masterImportTemplateValues.masterLocation.address.pincode'
    },
    {
        titleKey: 'masterLocationProperties.address.country',
        id: 'countryCode',
        accessor: ['address', 'countryCode'],
        templateValue:
            'masterImportTemplateValues.masterLocation.address.country'
    },
    {
        titleKey: 'masterLocationProperties.latitude',
        id: 'lat',
        accessor: ['latLng', 'lat'],
        templateValue: 'masterImportTemplateValues.masterLocation.latitude'
    },
    {
        titleKey: 'masterLocationProperties.longitude',
        id: 'lng',
        accessor: ['latLng', 'lng'],
        templateValue: 'masterImportTemplateValues.masterLocation.longitude'
    },
    {
        titleKey: 'masterLocationProperties.tat',
        id: 'transactionDuration',
        templateValue: 'masterImportTemplateValues.masterLocation.tat',
        processCell: LocationImportHelper.processMinutesToSeconds
    },
    {
        titleKey: 'masterLocationProperties.contactId',
        id: 'contactId',
        templateValue: 'masterImportTemplateValues.masterLocation.contactId',
        accessor: ['contactId', 'contactId'],
        serializeCell: LocationImportHelper.serializeContactId,
        getModelFieldAccessor: LocationImportHelper.getContactIdAccessor,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateContactIds,
            clientId
        }) => {
            LocationImportHelper.validateEntityIdAsync({
                validator: validateContactIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterLocationProperties.newContactId',
        id: 'newContactId',
        templateValue: 'masterImportTemplateValues.masterLocation.contactId',
        accessor: ['contact', 'id'],
        validateCell: LocationImportHelper.validateContact,
        getModelFieldAccessor: LocationImportHelper.getContactAccessor,
        serializeCell: LocationImportHelper.serializeContact
    },
    {
        titleKey: 'masterLocationProperties.contactName',
        id: 'contactName',
        templateValue: 'masterImportTemplateValues.masterLocation.contactName',
        accessor: ['contact', 'name'],
        validateCell: LocationImportHelper.validateContact,
        getModelFieldAccessor: LocationImportHelper.getContactAccessor,
        serializeCell: LocationImportHelper.serializeContact
    },
    {
        titleKey: 'masterLocationProperties.contactNumber',
        id: 'contactNumber',
        templateValue:
            'masterImportTemplateValues.masterLocation.contactNumber',
        accessor: ['contact', 'phoneNumber', 'phoneNumber'],
        validateCell: LocationImportHelper.validateContact,
        getModelFieldAccessor: LocationImportHelper.getContactAccessor,
        serializeCell: LocationImportHelper.serializeContact
    },
    {
        titleKey: 'masterLocationProperties.contactType',
        id: 'contactType',
        templateValue: 'masterImportTemplateValues.masterLocation.contactType',
        accessor: ['contact', 'type'],
        validateCell: LocationImportHelper.validateContact,
        getModelFieldAccessor: LocationImportHelper.getContactAccessor,
        serializeCell: LocationImportHelper.serializeContact
    },
    {
        titleKey: 'masterLocationProperties.contactTeams',
        id: 'contactTeams',
        templateValue: 'masterImportTemplateValues.masterLocation.contactTeams',
        accessor: ['contact', 'teams'],
        processCell: LocationImportHelper.processContactTeams,
        validateCell: LocationImportHelper.validateContact,
        serializeCell: LocationImportHelper.serializeContact,
        getModelFieldAccessor: LocationImportHelper.getContactAccessor
    },
    {
        titleKey: 'masterLocationProperties.skillId',
        id: 'skills',
        templateValue: 'masterImportTemplateValues.masterLocation.skills',
        processCell: LocationImportHelper.processSkills,
        validateCell: LocationImportHelper.validateSkills,
        serializeCell: LocationImportHelper.serializeSkills
    },
    {
        titleKey: 'masterLocationProperties.priority',
        id: 'priority',
        templateValue: 'masterImportTemplateValues.masterLocation.priority'
    },
    {
        titleKey: 'masterLocationProperties.crossDockServiceable',
        id: 'crossDockServiceable',
        templateValue:
            'masterImportTemplateValues.masterLocation.crossDockServiceable',
        accessor: ['crossDockInfo', 'serviceable'],
        isHidden: ({ isFieldHidden }) => isFieldHidden('crossDockId'),
        processCell: LocationImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterLocationProperties.crossDockIds',
        id: 'crossDockIds',
        templateValue: 'masterImportTemplateValues.masterLocation.crossDockIds',
        accessor: ['crossDockInfo', 'crossDockIds'],
        isHidden: ({ isFieldHidden }) => isFieldHidden('crossDockId'),
        serializeCell: LocationImportHelper.serializeHomebaseId,
        processCell: LocationImportHelper.processHomebaseIds,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateHomebaseIds,
            clientId
        }) => {
            LocationImportHelper.validateEntityIdAsync({
                validator: validateHomebaseIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterVehicleProperties.vehicleModelId',
        id: 'vehicleModelId',
        accessor: ['vehicleModelId', 'modelId'],
        isHidden: ({ isFieldHidden }) => isFieldHidden('vehicleModelId'),
        templateValue:
            'masterImportTemplateValues.masterVehicle.vehicleModelId',
        serializeCell: LocationImportHelper.serializeVehicleModelId,
        getModelFieldAccessor: LocationImportHelper.getVehicleModelIdAccessor,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateVehicleModelId,
            clientId,
            t
        }) => {
            LocationImportHelper.validateEntityIdAsync({
                validator: validateVehicleModelId,
                entityNameId: 'vehicleModelMaster',
                clientId,
                onSuccess,
                onFailure,
                entity,
                header,
                t
            });
        }
    }
]);

export default LOCATION_IMPORT_FORMAT;
